import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Divider, ClickAwayListener, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { sanitizeTaskHtml } from "../utils/richText";

const TEXT_COLORS = [
  "#000000", "#424242", "#d32f2f", "#c2185b", "#7b1fa2", "#1976d2",
  "#0288d1", "#00796b", "#388e3c", "#f57c00", "#e65100", "#5d4037",
];
const HIGHLIGHT_COLORS = [
  "transparent", "#fff59d", "#ffcc80", "#ef9a9a", "#f48fb1", "#ce93d8",
  "#90caf9", "#80deea", "#a5d6a7", "#e0e0e0",
];
// قيم execCommand("fontSize") من 1 إلى 7
const FONT_SIZES = [
  { value: 2, label: "صغير", px: 13 },
  { value: 3, label: "عادي", px: 16 },
  { value: 4, label: "متوسط", px: 18 },
  { value: 5, label: "كبير", px: 24 },
  { value: 6, label: "كبير جداً", px: 32 },
];
const STATE_COMMANDS = ["bold", "italic", "underline", "strikeThrough", "insertOrderedList", "insertUnorderedList"];

// زر الأدوات معرّف برّه المحرر حتى ما ينعاد إنشاؤه بكل رندر (كان يضيّع الضغطة)
const ToolBtn = memo(function ToolBtn({ title, onRun, icon, active, disabled, children }) {
  return (
    <Tooltip title={title} disableInteractive>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          // preventDefault يخلي التركيز والتحديد داخل منطقة الكتابة
          onMouseDown={(e) => {
            e.preventDefault();
            onRun(e);
          }}
          sx={{
            borderRadius: 1,
            bgcolor: active ? "rgba(25,118,210,0.15)" : "transparent",
            color: active ? "primary.main" : "text.primary",
          }}
        >
          {icon}
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
});

const dropdownSx = {
  position: "absolute",
  top: "100%",
  mt: 0.5,
  zIndex: 10,
  bgcolor: "#fff",
  borderRadius: 1.5,
  boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
  border: "1px solid #e0e0e0",
};

/**
 * محرر نص بسيط على طريقة Word (بدون مكتبات إضافية).
 * - initialHtml: يُحمّل مرة وحدة عند الفتح (غيّر key لإعادة التحميل)
 * - onChange({ html, text }): يُستدعى بعد توقف الكتابة لحظة (وفوراً عند ترك المحرر)
 *   حتى ما تنعاد رسمة صفحة التاسكات كلها مع كل حرف.
 */
export default function RichTextEditor({ initialHtml = "", onChange, disabled = false, placeholder = "اكتب وصف التاسك..." }) {
  const editorRef = useRef(null);
  const savedRange = useRef(null);
  const debounceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [openMenu, setOpenMenu] = useState(null); // null | "size" | "foreColor" | "hiliteColor"
  const [active, setActive] = useState({});
  const [isEmpty, setIsEmpty] = useState(true);

  const flushChange = useCallback(() => {
    clearTimeout(debounceRef.current);
    const el = editorRef.current;
    if (!el) return;
    onChangeRef.current?.({
      html: el.innerHTML,
      text: el.innerText.replace(/\n{3,}/g, "\n\n").trim(),
    });
  }, []);

  const scheduleChange = useCallback(() => {
    const el = editorRef.current;
    if (el) setIsEmpty(!el.innerText.trim() && !el.querySelector("li"));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushChange, 300);
  }, [flushChange]);

  useEffect(() => {
    const el = editorRef.current;
    if (el) {
      el.innerHTML = sanitizeTaskHtml(initialHtml);
      setIsEmpty(!el.innerText.trim());
    }
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // نتابع التحديد: نحفظه (للقوائم المنسدلة) ونحدّث حالة أزرار B/I/U بس إذا تغيّرت فعلاً
  useEffect(() => {
    const onSelectionChange = () => {
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return;
      savedRange.current = sel.getRangeAt(0).cloneRange();
      const next = {};
      STATE_COMMANDS.forEach((c) => {
        try {
          next[c] = document.queryCommandState(c);
        } catch {
          next[c] = false;
        }
      });
      setActive((prev) => (STATE_COMMANDS.some((c) => prev[c] !== next[c]) ? next : prev));
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  const exec = useCallback(
    (command, value = null) => {
      const el = editorRef.current;
      if (disabled || !el) return;
      if (document.activeElement !== el) el.focus();
      const sel = window.getSelection();
      if (savedRange.current && (!sel.rangeCount || !el.contains(sel.anchorNode))) {
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
      }
      document.execCommand("styleWithCSS", false, command !== "fontSize");
      document.execCommand(command, false, value);
      scheduleChange();
    },
    [disabled, scheduleChange]
  );

  // اللصق كنص عادي حتى ما تنجلب تنسيقات غريبة من مواقع/واتساب
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    scheduleChange();
  };

  const toggleMenu = (name) => setOpenMenu((m) => (m === name ? null : name));
  const colors = openMenu === "foreColor" ? TEXT_COLORS : HIGHLIGHT_COLORS;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "rgba(0,0,0,0.23)",
        borderRadius: 1,
        "&:focus-within": { borderColor: "primary.main", boxShadow: "0 0 0 1px #1976d2" },
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {/* شريط الأدوات */}
      <ClickAwayListener onClickAway={() => openMenu && setOpenMenu(null)}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.25,
            px: 1,
            py: 0.5,
            bgcolor: "#f5f7fa",
            borderBottom: "1px solid #e0e0e0",
            borderRadius: "4px 4px 0 0",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <ToolBtn title="حجم الخط" disabled={disabled} active={openMenu === "size"} onRun={() => toggleMenu("size")} icon={<FormatSizeIcon fontSize="small" />}>
              <Typography variant="caption" sx={{ mx: 0.5 }}>الحجم ▾</Typography>
            </ToolBtn>
            {openMenu === "size" && (
              <Box sx={{ ...dropdownSx, right: 0, py: 0.5, minWidth: 170 }}>
                {FONT_SIZES.map((s) => (
                  <Box
                    key={s.value}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setOpenMenu(null);
                      exec("fontSize", s.value);
                    }}
                    sx={{ px: 2, py: 0.75, cursor: "pointer", fontSize: s.px, "&:hover": { bgcolor: "#f0f4ff" } }}
                  >
                    {s.label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolBtn title="غامق (Ctrl+B)" disabled={disabled} active={active.bold} onRun={() => exec("bold")} icon={<FormatBoldIcon fontSize="small" />} />
          <ToolBtn title="مائل (Ctrl+I)" disabled={disabled} active={active.italic} onRun={() => exec("italic")} icon={<FormatItalicIcon fontSize="small" />} />
          <ToolBtn title="تسطير (Ctrl+U)" disabled={disabled} active={active.underline} onRun={() => exec("underline")} icon={<FormatUnderlinedIcon fontSize="small" />} />
          <ToolBtn title="يتوسطه خط" disabled={disabled} active={active.strikeThrough} onRun={() => exec("strikeThrough")} icon={<StrikethroughSIcon fontSize="small" />} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Box sx={{ position: "relative", display: "flex" }}>
            <ToolBtn title="لون النص" disabled={disabled} active={openMenu === "foreColor"} onRun={() => toggleMenu("foreColor")} icon={<FormatColorTextIcon fontSize="small" sx={{ color: "#d32f2f" }} />} />
            <ToolBtn title="تظليل" disabled={disabled} active={openMenu === "hiliteColor"} onRun={() => toggleMenu("hiliteColor")} icon={<FormatColorFillIcon fontSize="small" sx={{ color: "#f9a825" }} />} />
            {(openMenu === "foreColor" || openMenu === "hiliteColor") && (
              <Box sx={{ ...dropdownSx, right: 0, p: 1.5, display: "grid", gridTemplateColumns: "repeat(6, 28px)", gap: 1 }}>
                {colors.map((c) => (
                  <Box
                    key={c}
                    title={c === "transparent" ? "بدون تظليل" : c}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const mode = openMenu;
                      setOpenMenu(null);
                      exec(mode, c);
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      border: "2px solid #fff",
                      boxShadow: "0 0 0 1px #bdbdbd",
                      cursor: "pointer",
                      bgcolor: c,
                      backgroundImage:
                        c === "transparent"
                          ? "linear-gradient(45deg, transparent 45%, #d32f2f 45%, #d32f2f 55%, transparent 55%)"
                          : "none",
                      "&:hover": { transform: "scale(1.15)" },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolBtn title="قائمة مرقّمة" disabled={disabled} active={active.insertOrderedList} onRun={() => exec("insertOrderedList")} icon={<FormatListNumberedIcon fontSize="small" />} />
          <ToolBtn title="قائمة نقطية" disabled={disabled} active={active.insertUnorderedList} onRun={() => exec("insertUnorderedList")} icon={<FormatListBulletedIcon fontSize="small" />} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolBtn title="محاذاة لليمين" disabled={disabled} onRun={() => exec("justifyRight")} icon={<FormatAlignRightIcon fontSize="small" />} />
          <ToolBtn title="توسيط" disabled={disabled} onRun={() => exec("justifyCenter")} icon={<FormatAlignCenterIcon fontSize="small" />} />
          <ToolBtn title="محاذاة لليسار" disabled={disabled} onRun={() => exec("justifyLeft")} icon={<FormatAlignLeftIcon fontSize="small" />} />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ToolBtn title="تراجع" disabled={disabled} onRun={() => exec("undo")} icon={<UndoIcon fontSize="small" />} />
          <ToolBtn title="إعادة" disabled={disabled} onRun={() => exec("redo")} icon={<RedoIcon fontSize="small" />} />
          <ToolBtn title="مسح التنسيق" disabled={disabled} onRun={() => exec("removeFormat")} icon={<FormatClearIcon fontSize="small" />} />
        </Box>
      </ClickAwayListener>

      {/* منطقة الكتابة */}
      <Box sx={{ position: "relative" }}>
        {isEmpty && (
          <Box sx={{ position: "absolute", top: 16, right: 16, color: "#9e9e9e", pointerEvents: "none" }}>
            {placeholder}
          </Box>
        )}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          dir="rtl"
          spellCheck={false}
          className="artika-rte"
          onInput={scheduleChange}
          onBlur={flushChange}
          onPaste={handlePaste}
          style={{
            minHeight: 260,
            maxHeight: "55vh",
            overflowY: "auto",
            resize: "vertical",
            padding: 16,
            fontSize: 16,
            lineHeight: 1.9,
            outline: "none",
            background: "#fff",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            borderRadius: "0 0 4px 4px",
          }}
        />
        <style>{`.artika-rte ol, .artika-rte ul { padding-right: 28px; padding-left: 0; margin: 4px 0; list-style-position: outside; } .artika-rte ol { list-style-type: decimal; } .artika-rte ul { list-style-type: disc; } .artika-rte ul ul { list-style-type: circle; } .artika-rte a { color: #1976d2; }`}</style>
      </Box>
    </Box>
  );
}
